// src/lib/pieces.mjs
// ───────────────────────────────────────────────────────────────────────────
// 🧩 Agregado de PIEZAS a partir de planchas (PLAN-TERMINAL-OPERADOR W1 · B1b)
//
// Las piezas NO tienen colección propia: viven embebidas en `sheets[].pieces[]`.
// Su inventario (requerido/impreso/cortado, en qué planchas está) se AGREGA
// cruzando las planchas del job. Esta era la lógica DENTRO de PiecesInventoryTab;
// se extrae aquí PURA y reutilizable ("reusar, no duplicar") para que la consuman
// también `/scan` (búsqueda de pieza) y el backfill de `pieceCodes`.
//
// Es .mjs (ESM puro, sin React) a propósito: lo importan PiecesInventoryTab y
// ScanningStationView (CRA/webpack), los unit tests y el script de backfill (Node).
// ───────────────────────────────────────────────────────────────────────────

/**
 * Agrega las piezas de una lista de planchas. PURO.
 * @param {Array<object>} sheets  planchas con { hri, id, repetitions, isPrinting,
 *                                 printedCount, cutCount, pieces:[{code,name,qtyPerSheet,previewUrl}] }
 * @return {Array<object>}  piezas con { code, name, previewUrl, totalQuantity,
 *                            printedQuantity, cutQuantity, sheets:Set<hri>, isPrinting }
 */
export function aggregatePiecesFromSheets(sheets) {
  const map = new Map();
  (sheets || []).forEach((sheet) => {
    const sheetHRI = sheet.hri || sheet.id;
    (sheet.pieces || []).forEach((piece) => {
      if (!piece.code) return;
      const qtyPer = piece.qtyPerSheet || 1;
      const totalForSheet = qtyPer * (sheet.repetitions || 1);
      if (!map.has(piece.code)) {
        map.set(piece.code, {
          ...piece, totalQuantity: 0, printedQuantity: 0, cutQuantity: 0,
          sheets: new Set(), isPrinting: false,
        });
      }
      const agg = map.get(piece.code);
      agg.totalQuantity += totalForSheet;
      agg.sheets.add(sheetHRI);
      if (sheet.isPrinting) agg.isPrinting = true;
      agg.printedQuantity += qtyPer * (sheet.printedCount || 0);
      agg.cutQuantity += qtyPer * (sheet.cutCount || 0);
    });
  });
  return Array.from(map.values());
}

/**
 * Estado de producción de una pieza agregada (mismo criterio que el de
 * PiecesInventoryTab.getProductionStatus). Devuelve { text, style } para reuso en
 * tabla (clases Tailwind claras) y un `text` legible para /scan.
 * @param {object} piece
 * @return {{text:string, style:string}}
 */
export function pieceProductionStatus(piece) {
  if (!piece) return { text: 'Pendiente', style: 'bg-gray-100 text-gray-800' };
  if (piece.cutQuantity >= piece.totalQuantity) return { text: 'Producido', style: 'bg-green-100 text-green-800' };
  if (piece.printedQuantity >= piece.totalQuantity) return { text: 'Listo para Cortar', style: 'bg-blue-100 text-blue-800' };
  if (piece.printedQuantity > 0) return { text: 'Impresión Parcial', style: 'bg-purple-100 text-purple-800' };
  return { text: 'Pendiente', style: 'bg-gray-100 text-gray-800' };
}

/** Busca una pieza por código (case-insensitive) agregada sobre las planchas. */
export function findPieceAcrossSheets(sheets, code) {
  const target = String(code || '').trim().toLowerCase();
  if (!target) return null;
  return aggregatePiecesFromSheets(sheets).find((p) => String(p.code || '').toLowerCase() === target) || null;
}

/**
 * Lista de códigos de pieza (minúsculas, deduplicados) de una plancha, para
 * DENORMALIZAR en `sheets.pieceCodes` y poder buscar piezas con UNA collection-group
 * query (`array-contains`), igual que `hriSearch` para planchas (§9.3 Pilar 3).
 * @param {Array<{code:string}>} pieces
 * @return {string[]}
 */
export function collectPieceCodes(pieces) {
  const set = new Set();
  (pieces || []).forEach((p) => {
    const c = String((p && p.code) || '').trim().toLowerCase();
    if (c) set.add(c);
  });
  return Array.from(set);
}
